// excel-export.js
//
// Projectexport als Excel (.xlsx): één tabblad per soort onderdeel, voor wie gegevens wil
// filteren of narekenen. Het bestand is voor mensen, dus in de cellen staan termen ('zand',
// 'grijs') en geen TerraIndex-codes ('Z', 'GR'). De vertaling loopt via codeLabel, zodat er
// geen tweede vertaaltabel ontstaat.
//
// Afspraken:
// - Boringen: één rij per boring. Op elk ander tabblad is de eerste kolom de boringnaam.
// - Diepten als getal in meters (opgeslagen in cm, dus /100); de eenheid staat in de kolomnaam.
// - Meetwaarden zijn echte getallen, zodat Excel ermee kan rekenen.
//
// Geen xlsx-bibliotheek: een .xlsx is een zip met een handvol XML-bestanden. We schrijven alleen
// (nooit inlezen) en fflate zit al in de backoffice. Die laden we pas bij het exporteren in.

import { formatBzb, nen6693 } from '@mv/models';
import { codeLabel } from './codelabels.js';

const SHEET_NAMES = ['Boringen', 'Boorlagen', 'Grondmonsters', 'Peilbuizen', 'Watermonsters', 'Flessen', 'Afwerking'];

/** Getal of null; ook '1,5' en '1.5' als tekst. Nooit NaN/Infinity in een cel. */
const num = (v) => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
/** cm → m, zonder afrondingsruis (123 cm → 1.23, niet 1.2300000000000002). */
const cmToM = (v) => {
  const n = num(v);
  return n == null ? null : Math.round(n * 10) / 1000;
};
const yesNo = (v) => (v === true || v === 1 || v === '1' ? 'ja' : v === false || v === 0 || v === '0' ? 'nee' : null);
const text = (v) => (v == null || v === '' ? null : String(v));
/** Term bij een code; is die er niet, dan de code zelf (beter dan een lege cel). */
const term = (field, value, standard) => codeLabel(field, value, standard) ?? text(value);

function colourText(main, shade, sub, standard, prefix) {
  const parts = [
    codeLabel(`${prefix}Shade`, shade, standard) ?? text(shade),
    codeLabel(`${prefix}Main`, main, standard) ?? text(main),
    codeLabel(`${prefix}Sub`, sub, standard) ?? text(sub),
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
}

function admixText(layer, i, standard) {
  const admix = layer[`toevoeging${i}`];
  if (!admix) return null;
  const grade = codeLabel(`gradatie${i}`, layer[`gradatie${i}`], standard);
  const label = term(`toevoeging${i}`, admix, standard);
  return grade ? `${grade} ${label}` : label;
}

const active = (list) => (Array.isArray(list) ? list.filter((x) => x && !x.deletedAt) : []);

/**
 * Zet boringen om naar tabbladen met kopregel en rijen.
 *
 * @param {object[]} borings  platte boringen (API-vorm), eventueel met `_standard`
 * @param {{ coordinateSystemLabel?: string, heightDatumLabel?: string }} [opts]
 * @returns {{ name: string, columns: string[], rows: any[][] }[]}
 */
export function buildExcelSheets(borings, opts = {}) {
  const sys = opts.coordinateSystemLabel ? ` (${opts.coordinateSystemLabel})` : '';
  const datum = opts.heightDatumLabel ? ` ${opts.heightDatumLabel}` : '';
  const list = active(borings);

  const boringRows = [];
  const layerRows = [];
  const sampleRows = [];
  const wellRows = [];
  const waterRows = [];
  const bottleRows = [];
  const finishRows = [];

  for (const b of list) {
    const std = b._standard;
    const name = b.name || '';
    const layers = (b.layers || []).slice().sort((p, q) => (num(p.upperDepth) ?? 0) - (num(q.upperDepth) ?? 0));
    const endDepthCm = layers.length ? Math.max(...layers.map((l) => num(l.lowerDepth) ?? 0)) : null;

    boringRows.push([
      name, term('boringType', b.type, std), text(b.datum), text(b.boormeester),
      num(b.x), num(b.y), num(b.z),
      term('referencePlane', b.referencePlane, std), term('groundLevelType', b.groundLevelType, std),
      term('drillingSystem', b.drillingSystem, std), cmToM(endDepthCm),
      yesNo(b.gwsFound), cmToM(b.gwsCmMv), term('stopReason', b.stopReason, std), text(b.opmerking),
    ]);

    for (const l of layers) {
      layerRows.push([
        name, cmToM(l.upperDepth), cmToM(l.lowerDepth),
        term('soilType', l.soilType, std), term('mediaan', l.mediaan, std),
        admixText(l, 1, std), admixText(l, 2, std), admixText(l, 3, std),
        colourText(l.kleurMain, l.kleurShade, l.kleurSub, std, 'kleur'),
        (l.bzb || []).map((z) => formatBzb({ ti_bzb_type: z.code, type_naam: z.type, ti_bzb_gradation: z.gradation, ti_bzb_origin: z.origin })).filter(Boolean).join('; ') || null,
        [codeLabel('geurIntensity', l.geurIntensity, std), term('geurType', l.geurType, std)].filter(Boolean).join(' ') || null,
        term('oilWaterReaction', l.oilWaterReaction, std),
        term('drillingSystem', l.drillingSystem, std), text(l.opmerking),
      ]);
    }

    for (const m of active(b.monsters)) {
      sampleRows.push([
        name, text(m.name), text(m.identificatie), cmToM(m.upperDepth), cmToM(m.lowerDepth),
        text(m.type), term('packingType', m.packingType, std), yesNo(m.geroerd), text(m.datum), text(m.opmerking),
      ]);
    }

    const wells = active(b.peilbuizen);
    for (const p of wells) {
      wellRows.push([
        name, text(p.name), cmToM(p.filterTopCm), cmToM(p.filterBottomCm), num(p.diameter), num(p.innerDiameterMm),
        term('material', p.material, std), term('flowType', p.flowType, std),
        yesNo(p.filterSieveUsed), yesNo(p.unwatered), yesNo(p.rinsed),
        num(p.ph), num(p.ec), num(p.temperature), num(p.pumpedL), num(p.waterUsageL), num(p.ecWorkWater),
        term('referencePointType', p.referencePointType, std), num(p.topM), term('refTopCode', p.refTopCode, std),
        text(p.installedAt), text(p.remark),
      ]);
    }
    const wellName = new Map(wells.map((p) => [p.id, p.name]));

    for (const w of active(b.watermonsters)) {
      waterRows.push([
        name, text(w.name), text(w.date), text(w.time), text(w.sampler), text(w.matrix), text(wellName.get(w.peilbuisId)),
        cmToM(w.gwsBeforePrepumpCm), cmToM(w.gwsAfterPrepumpCm), cmToM(w.gwsBeforeSamplingCm),
        term('gwsReferencePoint', w.gwsReferencePoint, std),
        num(w.prepumpVolumeL), num(w.recommendedPrepumpVolumeL), num(w.flushVolumeL), num(w.flowRate),
        text(w.yield), text(w.pumpType), yesNo(w.aerated),
        num(w.ph), num(w.ec), num(w.temperature), num(w.oxygen), num(w.redox), num(w.turbidityPrepump), num(w.turbiditySample),
        colourText(w.colourMain, w.colourShade, w.colourSub, std, 'colour'), text(w.clarity), text(w.specialConstituents),
        yesNo(w.floatingLayer), num(w.floatingLayerThicknessMm), yesNo(w.sinkingLayer), num(w.sinkingLayerThicknessMm),
        text(w.transportDate), text(w.remark),
      ]);
      for (const f of active(w.bottles)) {
        bottleRows.push([
          name, text(w.name), text(f.name), text(f.barcode), yesNo(f.filtered), text(f.filterType),
          text(f.bottleType), text(f.analysisPackage), text(f.preservation), text(f.remark),
        ]);
      }
    }

    const afw = b.afwerking || {};
    for (const f of afw.fillings || []) {
      finishRows.push([name, 'vulling', cmToM(f.van), cmToM(f.tot), term('fillingMaterial', f.materiaal, std), null, text(f.opmerking)]);
    }
    for (const o of afw.achtergelatenObjecten || []) {
      const kind = o.aard === 'achtergebleven' ? 'achtergebleven in de grond' : 'geplaatst in de grond';
      const what = [term('leftBehindType', o.type, std), text(o.materiaal)].filter(Boolean).join(', ') || null;
      finishRows.push([name, kind, cmToM(o.van), cmToM(o.tot), what, null, text(o.opmerking)]);
    }
    for (const t of afw.topCompletions || []) {
      const type = nen6693.bovenafwerkingMigrate(t.type, t.materiaal);
      finishRows.push([name, 'bovenafwerking', null, null, term('topCompletionType', type, std), cmToM(t.heightCm), null]);
    }
  }

  const columns = {
    Boringen: [
      'Boring', 'Type', 'Datum', 'Boormeester', `X${sys}`, `Y${sys}`, `Maaiveldhoogte (m${datum})`,
      'Referentievlak', 'Maaiveldtype', 'Boorsysteem', 'Einddiepte (m-mv)',
      'Grondwater aangetroffen', 'Grondwaterstand (m-mv)', 'Reden gestopt', 'Opmerking',
    ],
    Boorlagen: [
      'Boring', 'Bovenkant (m-mv)', 'Onderkant (m-mv)', 'Grondsoort', 'Mediaan',
      'Bijmenging 1', 'Bijmenging 2', 'Bijmenging 3', 'Kleur', 'Bijzondere bestanddelen',
      'Geur', 'Olie-waterreactie', 'Boorsysteem', 'Opmerking',
    ],
    Grondmonsters: [
      'Boring', 'Monster', 'Barcode', 'Bovenkant (m-mv)', 'Onderkant (m-mv)',
      'Type', 'Verpakking', 'Geroerd', 'Datum', 'Opmerking',
    ],
    Peilbuizen: [
      'Boring', 'Peilbuis', 'Filter bovenkant (m-mv)', 'Filter onderkant (m-mv)',
      'Buitendiameter (mm)', 'Binnendiameter (mm)', 'Materiaal', 'Doorloop',
      'Filterkous', 'Leeggezogen', 'Gespoeld',
      'pH (-)', 'EC (µS/cm)', 'Temperatuur (°C)', 'Afgepompt (l)', 'Werkwater (l)', 'EC werkwater (µS/cm)',
      'Referentiepunt', 'Bovenkant buis (m)', 'Referentie bovenkant', 'Geplaatst op', 'Opmerking',
    ],
    Watermonsters: [
      'Boring', 'Watermonster', 'Datum', 'Tijd', 'Monsternemer', 'Matrix', 'Peilbuis',
      'GWS voor voorpompen (m)', 'GWS na voorpompen (m)', 'GWS voor bemonstering (m)', 'Referentievlak GWS',
      'Voorgepompt volume (l)', 'Te voorpompen volume (l)', 'Spoelvolume (l)', 'Debiet (l/min)',
      'Opbrengst', 'Pomptype', 'Belucht',
      'pH (-)', 'EC (µS/cm)', 'Temperatuur (°C)', 'Zuurstof (mg/l)', 'Redox (mV)',
      'Troebelheid voorpompen (NTU)', 'Troebelheid monster (NTU)',
      'Kleur', 'Transparantie', 'Bijzondere bestanddelen',
      'Drijflaag', 'Dikte drijflaag (mm)', 'Zaklaag', 'Dikte zaklaag (mm)',
      'Datum vervoer', 'Opmerking',
    ],
    Flessen: [
      'Boring', 'Watermonster', 'Fles', 'Barcode', 'Gefiltreerd', 'Filtertype',
      'Flestype', 'Analysepakket', 'Conservering', 'Opmerking',
    ],
    Afwerking: [
      'Boring', 'Onderdeel', 'Van (m-mv)', 'Tot (m-mv)', 'Materiaal / type', 'Hoogte boven maaiveld (m)', 'Opmerking',
    ],
  };
  const rows = {
    Boringen: boringRows, Boorlagen: layerRows, Grondmonsters: sampleRows, Peilbuizen: wellRows,
    Watermonsters: waterRows, Flessen: bottleRows, Afwerking: finishRows,
  };
  return SHEET_NAMES.map((n) => ({ name: n, columns: columns[n], rows: rows[n] }));
}

// ─── xlsx schrijven ─────────────────────────────────────────────────────────

/** XML-escape, en tekens die XML 1.0 niet toestaat eruit (anders weigert Excel het bestand). */
const esc = (s) => String(s)
  // eslint-disable-next-line no-control-regex
  .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Kolomletter bij index 0 → A, 25 → Z, 26 → AA. */
export const columnLetter = (i) => {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
};

function cellXml(value, ref, style) {
  if (value == null || value === '') return '';
  const s = style ? ` s="${style}"` : '';
  if (typeof value === 'number' && Number.isFinite(value)) return `<c r="${ref}"${s}><v>${value}</v></c>`;
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${esc(value)}</t></is></c>`;
}

function sheetXml({ columns, rows }) {
  const all = [columns, ...rows];
  const body = all.map((row, r) => {
    const cells = row.map((v, c) => cellXml(v, `${columnLetter(c)}${r + 1}`, r === 0 ? 1 : 0)).join('');
    return `<row r="${r + 1}">${cells}</row>`;
  }).join('');
  // Kopregel bevriezen, zodat de kolomnamen in beeld blijven bij scrollen.
  const pane = '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>';
  const cols = `<cols>${columns.map((h, i) => `<col min="${i + 1}" max="${i + 1}" width="${Math.max(10, Math.min(40, String(h).length + 2))}" customWidth="1"/>`).join('')}</cols>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${pane}${cols}<sheetData>${body}</sheetData></worksheet>`;
}

/**
 * Bouw de bestanden van een .xlsx (pad → XML-tekst). Los van de zip, zodat het testbaar is.
 * @param {{ name: string, columns: string[], rows: any[][] }[]} sheets
 * @returns {Record<string, string>}
 */
export function buildXlsxFiles(sheets) {
  const NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
  const PKG = 'http://schemas.openxmlformats.org/package/2006/relationships';
  const head = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  const files = {
    '[Content_Types].xml': `${head}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
      + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
      + '<Default Extension="xml" ContentType="application/xml"/>'
      + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
      + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
      + sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')
      + '</Types>',
    '_rels/.rels': `${head}<Relationships xmlns="${PKG}"><Relationship Id="rId1" Type="${NS}/officeDocument" Target="xl/workbook.xml"/></Relationships>`,
    'xl/workbook.xml': `${head}<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="${NS}"><sheets>`
      + sheets.map((s, i) => `<sheet name="${esc(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')
      + '</sheets></workbook>',
    'xl/_rels/workbook.xml.rels': `${head}<Relationships xmlns="${PKG}">`
      + sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="${NS}/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')
      + `<Relationship Id="rId${sheets.length + 1}" Type="${NS}/styles" Target="styles.xml"/></Relationships>`,
    // Minimaal: stijl 0 = standaard, stijl 1 = vet (kopregel).
    'xl/styles.xml': `${head}<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
      + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
      + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
      + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
      + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
      + '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>'
      + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
      + '</styleSheet>',
  };
  sheets.forEach((s, i) => { files[`xl/worksheets/sheet${i + 1}.xml`] = sheetXml(s); });
  return files;
}

/** De .xlsx als bytes. `zipSync`/`strToU8` komen uit fflate (meegegeven of dynamisch geladen). */
export async function buildXlsx(sheets, fflate) {
  const { zipSync, strToU8 } = fflate || await import('fflate');
  const files = buildXlsxFiles(sheets);
  const entries = Object.fromEntries(Object.entries(files).map(([path, xml]) => [path, strToU8(xml)]));
  return zipSync(entries, { level: 6 });
}

/** Bouw de export en laat de browser hem downloaden. */
export async function exportProjectExcel(borings, filename, opts = {}) {
  const bytes = await buildXlsx(buildExcelSheets(borings, opts));
  const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
}
